import type Combatant from "@/encounter/model/Combatant.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {Button} from "@/components/ui/button.tsx";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog.tsx";
import {Check, X} from "lucide-react";
import {useId, useRef, useState} from "react";
import {cn} from "@/lib/utils";
import TokenFramePreview from "../TokenFramePreview.tsx";
import {
    TOKEN_FRAME_STYLES,
    tokenFrameStyle
} from "../TokenFrameStyles.ts";

export default function CombatantTokenFrameSelect({combatant}: { combatant: Combatant }) {
    const {update} = useCombatants();
    const value = tokenFrameStyle(combatant.tokenFrame);
    const selectedStyle = TOKEN_FRAME_STYLES.find(style => style.id === value)!;
    const [open, setOpen] = useState(false);
    const triggerId = useId();
    const selectedButtonRef = useRef<HTMLButtonElement>(null);

    return <div className="shrink-0">
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button id={triggerId} type="button" variant="ghost"
                        className="size-24 rounded-full p-0"
                        title={`Token-Rahmen auswählen: ${selectedStyle.label}`}
                        aria-label={`Token-Rahmen auswählen: ${selectedStyle.label}`}>
                    <TokenFramePreview style={value} image={combatant.character.image} className="size-24"/>
                </Button>
            </DialogTrigger>
            <DialogContent showCloseButton={false}
                           className="flex max-h-[calc(100dvh-2rem)] flex-col sm:max-w-2xl"
                           onOpenAutoFocus={event => {
                               event.preventDefault();
                               selectedButtonRef.current?.focus();
                           }}>
                <DialogHeader className="relative pr-10 text-left">
                    <DialogTitle>Token-Rahmen auswählen</DialogTitle>
                    <DialogDescription>Aktuell: {selectedStyle.label}</DialogDescription>
                    <DialogClose asChild>
                        <Button type="button" variant="ghost" size="icon"
                                className="absolute -top-2 right-0" aria-label="Dialog schließen">
                            <X aria-hidden="true"/>
                        </Button>
                    </DialogClose>
                </DialogHeader>
                <div className="grid min-h-0 grid-cols-2 gap-3 overflow-y-auto p-1 sm:grid-cols-4">
                    {TOKEN_FRAME_STYLES.map(style => {
                        const selected = style.id === value;
                        return <Button key={style.id} type="button" variant="outline"
                                       ref={selected ? selectedButtonRef : undefined}
                                       aria-pressed={selected}
                                       className={cn(
                                           "relative h-auto min-w-0 flex-col gap-2 whitespace-normal rounded-xl p-3",
                                           selected && "border-primary bg-primary/10 ring-1 ring-primary",
                                       )}
                                       onClick={() => {
                                           if (!selected) void update([{...combatant, tokenFrame: style.id}]);
                                           setOpen(false);
                                       }}>
                            <TokenFramePreview style={style.id} image={combatant.character.image}
                                               className="w-28 max-w-full"/>
                            <span className="text-center">{style.label}</span>
                            {selected && <Check className="absolute right-2 top-2 text-primary" aria-hidden="true"/>}
                        </Button>;
                    })}
                </div>
                <DialogFooter className="items-center border-t pt-4 sm:justify-between">
                    <span className="text-sm text-muted-foreground">Ein Klick übernimmt den Rahmen.</span>
                    <DialogClose asChild>
                        <Button type="button" variant="outline">Schließen</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>;
}
